import { Page } from "./Page"
import { throttle } from "lodash"
import { dataReviewPerformance } from "./DataReviewPerformance"

export type TimeSeriesPageRequest = {
	pageId: number
	studyId: string
	patientId: string
	modalities: string[]
	startDate: Date | number
	endDate: Date | number
	numPoints: number
	resolution: string
}

export class TimeSeriesPageQueue<PageType extends Page<any>> {
	public loadQueue: PageType[] = []
	public unloadQueue: PageType[] = []
	public onSuccess = (page: PageType) => {}
	public onPageLoadStart = (page: PageType) => {}
	public allLoadedPages = new Map<number, PageType>()

	public activePageRequests = new Map<number, PageType>()
	private queueGeneration = 0
	private maxConcurrentRequests: number

	constructor(maxConcurrentRequests: number = 1) {
		this.maxConcurrentRequests = Math.max(1, maxConcurrentRequests)
	}

	push = (pages: PageType[]) => {
		const nonDuplicates = pages.filter(page => (
			!page.loading
			&& !this.loadQueue.find(queuedPage => page.id === queuedPage.id)
			&& !this.activePageRequests.has(page.id)
		))
		nonDuplicates.forEach(page => dataReviewPerformance.recordPageQueued(page.id))
		this.loadQueue.push(...nonDuplicates)
		this.drain()
	}

	unloadAllLoadedPages() {
		this.allLoadedPages.forEach(page => page.unload())
	}

	unloadOutside = throttle((neighborhood: PageType[]) => {
		const neighborhoodPages = new Set(neighborhood)

		this.loadQueue = this.loadQueue.filter(page => neighborhoodPages.has(page))
		this.unloadQueue.length = 0

		// Iterate over the keys of the Map
		for (const page of this.allLoadedPages.values()) {
			if (!neighborhoodPages.has(page)) {
				this.unloadQueue.push(page)
			}
		}

		// Delete the keys outside the loop to avoid modifying the Map while iterating
		for (const page of this.unloadQueue) {
			this.allLoadedPages.delete(page.id)
			page.unload()
		}

		for (const page of [...this.activePageRequests.values()]) {
			if (!neighborhoodPages.has(page)) {
				this.activePageRequests.delete(page.id)
				page.cancel()
			}
		}

		this.drain()
	}, 500)

	drain = () => {
		for (const page of [...this.activePageRequests.values()]) {
			if (page.needsToBeLoaded()) {
				this.activePageRequests.delete(page.id)
				this.loadQueue.unshift(page)
			}
		}

		while (this.loadQueue.length > 0 && this.activePageRequests.size < this.maxConcurrentRequests) {
			const pageRequest = this.loadQueue.shift()
			if (!pageRequest) {
				return
			}

			this.startPageRequest(pageRequest, this.queueGeneration)
		}
	}

	pop = this.drain

	private startPageRequest = (pageRequest: PageType, generation: number) => {
		if (this.activePageRequests.has(pageRequest.id)) {
			return
		}

		this.activePageRequests.set(pageRequest.id, pageRequest)
		dataReviewPerformance.recordPageLoadStarted(pageRequest.id)

		pageRequest.setOnPartialLoad((loadedPage: Page<any>) => {
			if (!this.isCurrentRequest(loadedPage as PageType, generation)) {
				return
			}

			this.onSuccess(loadedPage as PageType)
			this.allLoadedPages.set(loadedPage.id, loadedPage as PageType)
		})

		pageRequest.setOnFinishedLoading((loadedPage: Page<any>) => {
			if (!this.isCurrentRequest(loadedPage as PageType, generation)) {
				return
			}

			this.onSuccess(loadedPage as PageType)
			this.allLoadedPages.set(loadedPage.id, loadedPage as PageType)
			this.activePageRequests.delete(loadedPage.id)
			this.drain()
		})

		pageRequest.setOnLoadEarlyExit(() => {
			if (!this.isCurrentRequest(pageRequest, generation)) {
				return
			}

			this.activePageRequests.delete(pageRequest.id)
			this.drain()
		})

		try {
			pageRequest.load()
			this.onPageLoadStart(pageRequest)
		} catch (error) {
			console.error(error)
			pageRequest.unload()
			this.activePageRequests.delete(pageRequest.id)
			this.drain()
		}
	}

	private isCurrentRequest = (page: PageType, generation: number) => (
		generation === this.queueGeneration && this.activePageRequests.get(page.id) === page
	)

	cancel = () => {
		this.queueGeneration += 1
		for (const page of [...this.activePageRequests.values()]) {
			page.cancel()
		}
		this.activePageRequests.clear()
		this.clear()
	}

	clear = () => {
		this.loadQueue.length = 0
	}
}
